import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { api } from '../../services/api';

interface LikeButtonProps {
  promptId: number;
  initialLikeCount: number;
  initialIsLiked?: boolean;
  isAuthenticated?: boolean;
  onLikeChange?: (newLikeCount: number, isLiked: boolean) => void;
  className?: string;
  size?: 'sm' | 'md';
}

export const LikeButton: React.FC<LikeButtonProps> = ({
  promptId,
  initialLikeCount,
  initialIsLiked = false,
  isAuthenticated = false,
  onLikeChange,
  className,
  size = 'sm'
}) => {
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthMessage, setShowAuthMessage] = useState(false);

  const handleLikeToggle = async () => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setShowAuthMessage(true);
      setTimeout(() => setShowAuthMessage(false), 2500);
      return;
    }

    setIsLoading(true);
    const newIsLiked = !isLiked;
    const previousLikeCount = likeCount; // Store previous value for revert
    const newLikeCount = newIsLiked ? likeCount + 1 : Math.max(0, likeCount - 1);

    // Optimistic update
    setIsLiked(newIsLiked);
    setLikeCount(newLikeCount);
    onLikeChange?.(newLikeCount, newIsLiked);

    try {
      const updatedPrompt = newIsLiked
        ? await api.likePrompt(promptId)
        : await api.unlikePrompt(promptId);

      // Update with server response to ensure consistency
      setLikeCount(updatedPrompt.likes ?? newLikeCount);
      onLikeChange?.(updatedPrompt.likes ?? newLikeCount, newIsLiked);

    } catch (error) {
      console.error('Error toggling like:', error);

      // Revert optimistic update on error
      setIsLiked(!newIsLiked);
      setLikeCount(previousLikeCount);
      onLikeChange?.(previousLikeCount, !newIsLiked);
    } finally {
      setIsLoading(false);
    }
  };

  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <span className="relative inline-flex items-center">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleLikeToggle}
        disabled={isLoading}
        title={!isAuthenticated ? 'Sign in to like prompts' : isLiked ? 'Unlike this prompt' : 'Like this prompt'}
        className={cn(
          'h-auto p-1 gap-1 text-muted-foreground hover:text-foreground transition-colors',
          isLiked && 'text-red-500 hover:text-red-600',
          isLoading && 'opacity-50 cursor-not-allowed',
          className
        )}
        aria-label={`${isLiked ? 'Unlike' : 'Like'} this prompt (${likeCount} ${likeCount === 1 ? 'like' : 'likes'})`}
      >
        <Heart
          className={cn(
            iconSize,
            isLiked && 'fill-current'
          )}
          aria-hidden="true"
        />
        <span className={textSize}>
          {likeCount}
        </span>
      </Button>
      {showAuthMessage && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[11px] text-white shadow z-10">
          Sign in to like prompts
        </span>
      )}
    </span>
  );
};